import { httpStatusOf } from "@/core/http/HttpError";
import type { putToGCS } from "@/core/upload/putToGCS";
import type { LocalUpload, ServerClip } from "./clipList";
import type { CreateClipRequest, CreatedClip, VoiceTarget } from "./voiceService";

export interface SendClipInput {
  purpose: string;
  target: VoiceTarget;
  blob: Blob;
  mimeType: string;
  durationSeconds: number;
}

export interface SendClipDeps {
  createClip: (request: CreateClipRequest) => Promise<CreatedClip>;
  putToGCS: typeof putToGCS;
  submitClip: (clipId: string) => Promise<ServerClip>;
  signal: AbortSignal;
  onProgress: (fraction: number) => void;
  /** The server holds an uploading clip from here on. */
  onCreated: (clipId: string) => void;
  /** The blob is in GCS. Only submit is left. */
  onUploaded: () => void;
}

/** A PUT that reports no progress for this long is aborted and fails as retryable. */
export const PUT_STALL_MS = 60_000;

/**
 * Create, PUT, submit (TD-089). Resolves to the clip id once submitted and
 * rejects at the first step that fails, without running the rest.
 */
export async function sendClip(input: SendClipInput, deps: SendClipDeps): Promise<string> {
  const { blob } = input;
  const created = await deps.createClip({
    purpose: input.purpose,
    target: input.target,
    mimeType: input.mimeType,
    sizeBytes: blob.size,
    durationSeconds: input.durationSeconds,
  });
  deps.onCreated(created.clipId);
  // The URL is signed for the base type. Chrome's blob.type carries
  // ";codecs=opus", which GCS would answer with a 403.
  await putUnlessStalled(created.uploadUrl, blob, created.contentTypeHeader, deps);
  deps.onUploaded();
  await deps.submitClip(created.clipId);
  return created.clipId;
}

/**
 * putToGCS, aborted once no progress arrives for PUT_STALL_MS. A dead
 * connection would otherwise leave the entry uploading forever.
 */
async function putUnlessStalled(url: string, blob: Blob, contentType: string, deps: SendClipDeps): Promise<void> {
  const controller = new AbortController();
  const cancel = () => controller.abort();
  let stalled = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const arm = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      stalled = true;
      controller.abort();
    }, PUT_STALL_MS);
  };
  deps.signal.addEventListener("abort", cancel);
  arm();
  try {
    await deps.putToGCS(
      url,
      blob,
      contentType,
      (fraction) => {
        arm();
        deps.onProgress(fraction);
      },
      controller.signal,
    );
  } catch (error) {
    throw stalled ? new Error(`Upload stalled: no progress for ${PUT_STALL_MS / 1000} s`) : error;
  } finally {
    clearTimeout(timer);
    deps.signal.removeEventListener("abort", cancel);
  }
}

export type UploadStage = "create" | "put" | "submit";

/** The statuses create answers when the recording itself is unacceptable. */
const CREATE_REJECTED_STATUSES = new Set([400, 403, 413, 415]);

/**
 * A create the server refused can't succeed on retry, so it is discard only.
 * Anything else, at any stage, may succeed later.
 */
export function uploadFailureStatus(
  stage: UploadStage,
  error: unknown,
): Exclude<LocalUpload["status"], "uploading"> {
  const status = httpStatusOf(error);
  return stage === "create" && status !== null && CREATE_REJECTED_STATUSES.has(status)
    ? "upload_rejected"
    : "upload_failed";
}

export type ResubmitOutcome = "done" | "gone" | "failed";

/**
 * Submit again for a clip whose PUT already landed. A 409 means an earlier
 * submit reached the server, which owns the clip now. Creating a new clip
 * instead would duplicate the note.
 */
export async function resubmitClip(
  submitClip: (clipId: string) => Promise<unknown>,
  clipId: string,
): Promise<ResubmitOutcome> {
  try {
    await submitClip(clipId);
    return "done";
  } catch (error) {
    const status = httpStatusOf(error);
    if (status === 409) return "done";
    if (status === 404) return "gone";
    return "failed";
  }
}
